import { useState } from "react";
import { donutGradient } from "@/utils/unifiedSchoolManagementData";
import { toTitleCase } from "@/utils/toTitleCase";
import UsmRiskSchoolsModal from "@/components/UsmRiskSchoolsModal";
import TeacherAdequacySchoolsModal from "@/components/TeacherAdequacySchoolsModal";

const formatIn = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return value ?? "—";
  return Number(value).toLocaleString("en-IN");
};

const formatPct = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(2)}%`;
};

const formatPtr = (value) => {
  if (value == null || value === "—" || Number.isNaN(Number(value))) return value ?? "—";
  return Number(value).toFixed(2);
};

export const SectionTitle = ({ title, description, pill }) => (
  <div className="usm-section-title">
    <div>
      <h2>{toTitleCase(title)}</h2>
      {description && <p>{description}</p>}
    </div>
    {pill && <span className="usm-section-pill">{pill}</span>}
  </div>
);

export const PanelHead = ({ title, description, pill }) => (
  <div className="usm-ph">
    <div>
      <h3>{toTitleCase(title)}</h3>
      {description && <p>{description}</p>}
    </div>
    {pill && <span className="usm-pill">{pill}</span>}
  </div>
);

export const MetricCard = ({ label, value, sub }) => (
  <article className="usm-card">
    <small>{label}</small>
    <strong>{value}</strong>
    {sub && <span className="usm-card-sub">{sub}</span>}
  </article>
);

export const Tag = ({ children, tone = "" }) => (
  <span className={`usm-tag ${tone}`.trim()}>{children}</span>
);

const EmptyPanel = ({ message = "No data available for the selected scope." }) => (
  <div className="usm-empty">{message}</div>
);

const BarRows = ({ rows, valueKey = "pct" }) => {
  if (!rows?.length) return <EmptyPanel />;
  return (
    <div className="usm-bars">
      {rows.map((row) => (
        <div className="usm-barrow" key={`${row.label}-${row[valueKey]}`}>
          <div className="usm-label">{row.label}</div>
          <div className="usm-track">
            <div
              className={`usm-bar ${row.tone || ""}`.trim()}
              style={{ width: `${row[valueKey]}%` }}
            />
          </div>
          <div className="usm-bar-value">{row.display ?? `${row[valueKey]}%`}</div>
        </div>
      ))}
    </div>
  );
};

const BifurcationSummaryTable = ({ title, description, rows, scopeLabel }) => (
  <article className="usm-panel">
    <PanelHead title={title} description={description} pill={scopeLabel || "District scope"} />
    {!rows?.length ? (
      <EmptyPanel />
    ) : (
      <table>
        <thead>
          <tr>
            <th>Management</th>
            <th className="usm-num">Number</th>
            <th className="usm-num">Percentage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.bucket}-${row.number}`}>
              <td>{row.total ? <b>{row.bucket}</b> : row.bucket}</td>
              <td className="usm-num">{row.total ? <b>{row.number}</b> : row.number}</td>
              <td className="usm-num">{row.total ? <b>{row.percentage}</b> : row.percentage}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </article>
);

export const buildSummaryTableRows = (apiPayload, section) => {
  const summary = apiPayload?.summary?.find((s) => s.section === section);
  if (!summary?.rows?.length) return [];

  const rows = summary.rows.map((row) => ({
    bucket: row.bucket,
    number: formatIn(row.number),
    percentage: formatPct(row.percentage),
  }));

  const otherRow = apiPayload?.schools_without_enrolment
    ? {
        bucket: "Other / No enrolment record",
        number: formatIn(apiPayload.schools_without_enrolment),
        percentage: formatPct(
          (apiPayload.schools_without_enrolment / Math.max(apiPayload.grand_total?.schools || 1, 1)) * 100
        ),
      }
    : null;

  if (section === "School" && otherRow && Number(apiPayload.schools_without_enrolment) > 0) {
    rows.push(otherRow);
  }

  const grandKey = section === "School" ? "schools" : section === "Enrolment" ? "enrolments" : "teachers";
  rows.push({
    bucket: "Grand Total",
    number: formatIn(apiPayload.grand_total?.[grandKey]),
    percentage: "100.00%",
    total: true,
  });

  return rows;
};

export const buildBifurcationTableRows = (apiPayload) => {
  if (!apiPayload?.groups?.length) return [];

  const rows = [];
  apiPayload.groups.forEach((group) => {
    rows.push({
      rowType: "group",
      mgmt: "",
      management: group.group_label || group.group_title,
      schools: formatIn(group.schools),
      enrolments: formatIn(group.enrolments),
      teachers: formatIn(group.teachers),
      ptr: formatPtr(group.ptr),
    });
    (group.rows || []).forEach((detail) => {
      rows.push({
        rowType: "detail",
        mgmt: detail.mgmt_code,
        management: detail.management,
        schools: formatIn(detail.schools),
        enrolments: formatIn(detail.enrolments),
        teachers: formatIn(detail.teachers),
        ptr: formatPtr(detail.ptr),
      });
    });
  });

  if (apiPayload.grand_total) {
    rows.push({
      rowType: "total",
      mgmt: "",
      management: "Grand Total",
      schools: formatIn(apiPayload.grand_total.schools),
      enrolments: formatIn(apiPayload.grand_total.enrolments),
      teachers: formatIn(apiPayload.grand_total.teachers),
      ptr: formatPtr(apiPayload.grand_total.ptr),
    });
  }

  return rows;
};

export const OverviewScreen = ({ derived }) => {
  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;

  const { hero, coverage, coveragePct, prioritySnapshot } = derived;
  const gaugeStyle = {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--usm-primary) 0 ${coveragePct}%, rgba(37, 99, 235, 0.12) ${coveragePct}% 100%)`,
  };

  return (
    <>
      <section className="usm-hero">
        <div className="usm-hero-grid">
          <div>
            <div className="usm-gwrap">
              <div className="usm-gauge" style={gaugeStyle}>
                <div>
                  <strong>{coverage}</strong>
                  <span>KPI Coverage</span>
                </div>
              </div>
              <div>
                <h2>{toTitleCase(hero.title)}</h2>
                <p>{hero.description}</p>
                <div className="usm-badges">
                  {hero.badges.map((badge) => (
                    <span key={badge.text} className={`usm-badge ${badge.tone}`.trim()}>
                      {badge.text}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="usm-note">
              <b>Executive Story</b>
              <span>{hero.story}</span>
            </div>
          </div>
          <div className="usm-grid-3">
            {hero.kpis.map((kpi) => (
              <MetricCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} />
            ))}
          </div>
        </div>
      </section>

      <SectionTitle
        title="Priority KPI Snapshot"
        description="Key metrics derived from live management bifurcation data for the selected scope."
        pill="Unified summary"
      />
      <section className="usm-grid-4">
        {prioritySnapshot.map((kpi) => (
          <MetricCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} />
        ))}
      </section>
    </>
  );
};

export const ManagementMixScreen = ({ derived }) => {
  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;
  const { managementMix } = derived;

  return (
    <>
      <SectionTitle
        title="Management Mix"
        description="High-level management structure with school base, enrolment load and teacher deployment mix."
        pill="Govt vs Aided vs Unaided"
      />
      <section className="usm-grid-2">
        <article className="usm-panel">
          <PanelHead
            title="Schools by Management Type"
            description="Distribution of institutions across major management groups."
            pill="School base"
          />
          {managementMix.donut.length ? (
            <div className="usm-donutwrap">
              <div className="usm-donut" style={{ background: donutGradient(managementMix.donut) }} />
              <div className="usm-legend">
                {managementMix.donut.map((row) => (
                  <div className="usm-legendrow" key={row.label}>
                    <span>
                      <i className="usm-dot" />
                      {row.label}
                    </span>
                    <b>
                      {row.count.toLocaleString("en-IN")} · {row.pct}%
                    </b>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyPanel />
          )}
        </article>
        <article className="usm-panel">
          <PanelHead
            title="Management Comparison"
            description="Side-by-side comparison of scale and pressure across major management types."
            pill="Core mix KPIs"
          />
          {managementMix.comparison.length ? (
            <table>
              <thead>
                <tr>
                  <th>Management</th>
                  <th className="usm-num">Schools</th>
                  <th className="usm-num">Enrolments</th>
                  <th className="usm-num">Teachers</th>
                  <th className="usm-num">PTR</th>
                </tr>
              </thead>
              <tbody>
                {managementMix.comparison.map((row) => (
                  <tr key={row.management}>
                    <td>{row.management}</td>
                    <td className="usm-num">{row.schools}</td>
                    <td className="usm-num">{row.enrolments}</td>
                    <td className="usm-num">{row.teachers}</td>
                    <td className="usm-num usm-score">{row.ptr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyPanel />
          )}
        </article>
      </section>
      <section className="usm-grid-3">
        {managementMix.avgEnrolment.map((row) => (
          <MetricCard key={row.sub} label="Avg Enrolment / School" value={row.value} sub={row.sub} />
        ))}
      </section>
    </>
  );
};

export const ShareScreen = ({ derived }) => {
  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;
  const { shareAnalysis } = derived;

  return (
    <>
      <SectionTitle
        title="Share Analysis"
        description="School, enrolment and teacher share percentages plus concentration indicators."
        pill="Share KPIs"
      />
      <section className="usm-grid-3">
        <article className="usm-panel">
          <PanelHead title="School Share %" description="Distribution by count of institutions." pill="100%" />
          <BarRows rows={shareAnalysis.schoolShare} />
        </article>
        <article className="usm-panel">
          <PanelHead title="Enrolment Share %" description="Student load across management groups." pill="100%" />
          <BarRows rows={shareAnalysis.enrolmentShare} />
        </article>
        <article className="usm-panel">
          <PanelHead title="Teacher Share %" description="Teacher deployment across management groups." pill="100%" />
          <BarRows rows={shareAnalysis.teacherShare} />
        </article>
      </section>
      <section className="usm-grid-4">
        {shareAnalysis.cards.map((card) => (
          <MetricCard key={card.label} label={card.label} value={card.value} sub={card.sub} />
        ))}
      </section>
    </>
  );
};

export const SchoolsScreen = ({ apiPayload, scopeLabel, derived }) => (
  <>
    <SectionTitle
      title="Schools Screen"
      description="School count hierarchy and managementwise bifurcation summary from live data."
      pill="School distribution"
    />
    <section className="usm-grid-2">
      <article className="usm-panel">
        <PanelHead
          title="School-Count Hierarchy"
          description="Largest school-owning categories and management structure in scope."
          pill={scopeLabel || "Scope"}
        />
        {derived?.schoolHierarchy?.length ? (
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th className="usm-num">Schools</th>
                <th className="usm-num">Share</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {derived.schoolHierarchy.map((row) => (
                <tr key={row.category}>
                  <td>{row.category}</td>
                  <td className="usm-num">{row.schools.toLocaleString("en-IN")}</td>
                  <td className="usm-num">{row.share}</td>
                  <td>
                    <Tag tone={row.tag}>{row.status}</Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyPanel />
        )}
      </article>
      <BifurcationSummaryTable
        title="Managementwise Bifurcation – Schools"
        description="School count and share by management group."
        scopeLabel={scopeLabel}
        rows={buildSummaryTableRows(apiPayload, "School")}
      />
    </section>
  </>
);

export const EnrolmentScreen = ({ apiPayload, scopeLabel, derived }) => (
  <>
    <SectionTitle
      title="Enrolment Screen"
      description="Enrolment share, largest categories and detailed enrolment distribution."
      pill="Enrolment load"
    />
    <section className="usm-grid-2">
      <article className="usm-panel">
        <PanelHead
          title="Enrolment View"
          description="Student load across major management groups in scope."
          pill={scopeLabel || "Scope"}
        />
        <BarRows rows={derived?.enrolmentView?.bars} valueKey="width" />
        <div className="usm-statlist" style={{ marginTop: 18 }}>
          {(derived?.enrolmentView?.stats || []).map((stat) => (
            <div className="usm-stat" key={stat.label}>
              <b>{stat.label}</b>
              <span>{stat.value}</span>
            </div>
          ))}
        </div>
      </article>
      <BifurcationSummaryTable
        title="Managementwise Bifurcation – Enrolment"
        description="Enrolment count and share by management group."
        scopeLabel={scopeLabel}
        rows={buildSummaryTableRows(apiPayload, "Enrolment")}
      />
    </section>
  </>
);

export const TeachersScreen = ({ apiPayload, scopeLabel, derived }) => (
  <>
    <SectionTitle
      title="Teachers Screen"
      description="Teacher share, average teachers per school, and teacher distribution."
      pill="Teacher deployment"
    />
    <section className="usm-grid-2">
      <article className="usm-panel">
        <PanelHead title="Teacher Share and Deployment" description="Teacher deployment across major management types." pill={scopeLabel || "Scope"} />
        <BarRows rows={derived?.teachersView?.bars} valueKey="width" />
        <div className="usm-statlist" style={{ marginTop: 18 }}>
          {(derived?.teachersView?.stats || []).map((stat, index) => (
            <div className="usm-stat" key={`${stat.label}-${index}`}>
              <b>{stat.label}</b>
              <span>{stat.value}</span>
            </div>
          ))}
        </div>
      </article>
      <BifurcationSummaryTable
        title="Managementwise Bifurcation – Teachers"
        description="Teacher count and share by management group."
        scopeLabel={scopeLabel}
        rows={buildSummaryTableRows(apiPayload, "Teachers")}
      />
    </section>
  </>
);

export const PtrScreen = ({ derived, scopeLabel }) => {
  const [adequacyMetricModal, setAdequacyMetricModal] = useState(null);
  const activeAdequacyRow = derived?.ptrAdequacy?.find((row) => row.id === adequacyMetricModal) || null;

  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;

  return (
    <>
      <SectionTitle
        title="PTR and Teacher Adequacy"
        description="Overall PTR, PTR by management type, high-PTR watchlist and teacher shortage estimates."
        pill="PTR risk view"
      />
      <section className="usm-grid-4">
        {derived.ptrKpis.map((kpi) => (
          <MetricCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} />
        ))}
      </section>
      <section className="usm-grid-2">
        <article className="usm-panel">
          <PanelHead
            title="High PTR Watchlist"
            description="Categories requiring teacher adequacy review and targeted intervention."
            pill="PTR > 31"
          />
          {derived.ptrWatchlist.length ? (
            <table>
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="usm-num">PTR</th>
                  <th className="usm-num">Gap @ PTR 30</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {derived.ptrWatchlist.map((row) => (
                  <tr key={row.category}>
                    <td>{row.category}</td>
                    <td className="usm-num usm-score">{row.ptr}</td>
                    <td className="usm-num">{row.gap}</td>
                    <td>
                      <Tag tone={row.tag}>{row.status}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyPanel message="No management categories exceed PTR 31 in this scope." />
          )}
        </article>
        <article className="usm-panel">
          <PanelHead title="Teacher Adequacy Estimates" description="Planning estimates at different PTR targets." pill="Scenario planning" />
          <div className="usm-statlist">
            {derived.ptrAdequacy.map((row) => (
              <div
                className="usm-stat clickable"
                key={row.id || row.label}
                role="button"
                tabIndex={0}
                aria-label={`${row.label}: view contributing schools`}
                onClick={() => row.id && setAdequacyMetricModal(row.id)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && row.id) {
                    e.preventDefault();
                    setAdequacyMetricModal(row.id);
                  }
                }}
              >
                <b>{row.label}</b>
                <span>{row.value}</span>
              </div>
            ))}
          </div>
        </article>
      </section>

      <TeacherAdequacySchoolsModal
        open={Boolean(adequacyMetricModal)}
        metric={adequacyMetricModal}
        onClose={() => setAdequacyMetricModal(null)}
        scopeLabel={scopeLabel || derived.scopeLabel}
        districtValue={activeAdequacyRow?.districtValue}
        districtLabel={activeAdequacyRow?.districtLabel}
        districtSurplusPtr30={
          adequacyMetricModal === "net_position_ptr_30"
            ? Number(activeAdequacyRow?.districtValue) >= 0
            : undefined
        }
      />
    </>
  );
};

export const DrilldownScreen = ({ derived }) => {
  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;

  return (
    <>
      <SectionTitle
        title="Detailed Management Drilldown"
        description="Row-level drilldown for major management categories by enrolment scale and PTR."
        pill="Detailed KPI table"
      />
      <article className="usm-panel">
        <PanelHead
          title="Priority Management Categories"
          description="Top categories by enrolment concentration, PTR and governance relevance."
          pill={derived.scopeLabel}
        />
        <div className="usm-detailwrap">
          {derived.drilldownRows.length ? (
            <table className="usm-detail">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Management Name</th>
                  <th>Group</th>
                  <th className="usm-num">Schools</th>
                  <th className="usm-num">Enrolments</th>
                  <th className="usm-num">Teachers</th>
                  <th className="usm-num">PTR</th>
                  <th className="usm-num">Avg Students / School</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {derived.drilldownRows.map((row) => (
                  <tr key={row.code}>
                    <td>{row.code}</td>
                    <td>{row.name}</td>
                    <td>{row.group}</td>
                    <td className="usm-num">{row.schools}</td>
                    <td className="usm-num">{row.enrolments}</td>
                    <td className="usm-num">{row.teachers}</td>
                    <td className="usm-num usm-score">{row.ptr}</td>
                    <td className="usm-num">{row.avg}</td>
                    <td>
                      <Tag tone={row.tag}>{row.status}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyPanel />
          )}
        </div>
      </article>
    </>
  );
};

export const BifurcationScreen = ({ apiPayload, scopeLabel }) => {
  const rows = buildBifurcationTableRows(apiPayload);
  return (
    <>
      <SectionTitle
        title="Managementwise Bifurcation Detailed Table"
        description="Full management-code table with schools, enrolments, teachers and PTR."
        pill="Live bifurcation"
      />
      <article className="usm-panel">
        <PanelHead
          title="Detailed Management Table"
          description="All school, enrolment, teacher and PTR values by management code."
          pill={scopeLabel || "District scope"}
        />
        <div className="usm-detailwrap">
          {rows.length ? (
            <table className="usm-detail">
              <thead>
                <tr>
                  <th>Mgt</th>
                  <th>Management</th>
                  <th className="usm-num">Schools</th>
                  <th className="usm-num">Enrolments</th>
                  <th className="usm-num">Teachers</th>
                  <th className="usm-num">PTR</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={`${row.management}-${index}`}
                    className={
                      row.rowType === "group" ? "usm-group-row" : row.rowType === "total" ? "usm-total-row" : ""
                    }
                  >
                    <td>{row.mgmt}</td>
                    <td>{row.management}</td>
                    <td className="usm-num">{row.schools}</td>
                    <td className="usm-num">{row.enrolments}</td>
                    <td className="usm-num">{row.teachers}</td>
                    <td className="usm-num">{row.ptr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyPanel />
          )}
        </div>
      </article>
    </>
  );
};

export const ActionsScreen = ({ derived, scopeLabel }) => {
  const [selectedRisk, setSelectedRisk] = useState(null);

  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;

  return (
    <>
      <SectionTitle
        title="Risk and Action Screen"
        description="Actionable alerts and executive action queue derived from live bifurcation metrics."
        pill="Priority actions"
      />
      <section className="usm-grid-2">
        <article className="usm-panel">
          <PanelHead
            title="Risk Register"
            description="Click a risk row to view the underlying school list."
            pill="KPI alerts"
          />
          {derived.risks.length ? (
            <table className="usm-risk-table">
              <thead>
                <tr>
                  <th>Risk</th>
                  <th>KPI Trigger</th>
                  <th className="usm-num">Value</th>
                  <th>Owner</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {derived.risks.map((row) => (
                  <tr
                    key={row.id || row.risk}
                    className="usm-risk-row"
                    onClick={() => setSelectedRisk(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedRisk(row);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`View schools for ${row.risk}`}
                  >
                    <td>{row.risk}</td>
                    <td>{row.trigger}</td>
                    <td className="usm-num">{row.value}</td>
                    <td>{row.owner}</td>
                    <td>
                      <Tag tone={row.tag}>{row.status}</Tag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyPanel message="No risk thresholds triggered for this scope." />
          )}
        </article>
        <article className="usm-panel">
          <PanelHead
            title="Recommended Action Queue"
            description="Focused executive actions derived from the live KPI set."
            pill="Priority"
          />
          <div className="usm-actions">
            {derived.actionQueue.map((action) => (
              <div className="usm-action" key={action.num}>
                <div className={`usm-ico ${action.tone}`.trim()}>{action.num}</div>
                <div>
                  <h4>{action.title}</h4>
                  <p>{action.text}</p>
                </div>
              </div>
            ))}
            <div className="usm-actfoot">
              <b>Next Review Recommendation</b>
              <span>
                {derived.reviewNote ||
                  "Use this unified dashboard as the single review screen for management structure, bifurcation health, teacher adequacy and data quality during district and state governance meetings."}
              </span>
            </div>
          </div>
        </article>
      </section>
      <UsmRiskSchoolsModal
        open={Boolean(selectedRisk)}
        risk={selectedRisk}
        onClose={() => setSelectedRisk(null)}
        scopeLabel={scopeLabel || derived.scopeLabel}
      />
    </>
  );
};

export const QualityScreen = ({ derived }) => {
  if (!derived) return <EmptyPanel message="Loading management bifurcation data…" />;
  const { dataQuality } = derived;

  return (
    <>
      <SectionTitle
        title="Data Quality Screen"
        description="Clean-up and governance metrics for trustworthy management dashboard reporting."
        pill="Quality controls"
      />
      <section className="usm-grid-4">
        {dataQuality.kpis.map((kpi) => (
          <MetricCard key={kpi.label} label={kpi.label} value={kpi.value} sub={kpi.sub} />
        ))}
      </section>
      <section className="usm-grid-2">
        <article className="usm-panel">
          <PanelHead
            title="Data Quality Checklist"
            description="Recommended checks before publishing or refreshing the live dashboard."
            pill="Checklist"
          />
          <table>
            <thead>
              <tr>
                <th>Check</th>
                <th>Description</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {dataQuality.checks.map((row) => (
                <tr key={row.check}>
                  <td>{row.check}</td>
                  <td>{row.description}</td>
                  <td>
                    <Tag tone={row.tag}>{row.status}</Tag>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
        <article className="usm-panel">
          <PanelHead title="Publish-Ready Notes" description="Interpretation guidance for users reading the merged dashboard." pill="Guidance" />
          <div className="usm-statlist">
            {dataQuality.publishNotes.map((note) => (
              <div className="usm-stat" key={note.label}>
                <b>{note.label}</b>
                <span>{note.value}</span>
              </div>
            ))}
          </div>
        </article>
      </section>
    </>
  );
};
